'use client';

// Shared phone control: a searchable country-code dropdown + national-number
// input (ISS-02 / ISS-18). Reused by every lead form so the behaviour and a11y
// are identical everywhere. Real flag images via <FlagIcon> (emoji flags don't
// render on Windows). Keyboard + screen-reader friendly: the trigger is a real
// button with aria-haspopup="listbox"/aria-expanded, the panel is a listbox
// with role="option" rows, Escape/outside-click closes, 44px tap targets.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import FlagIcon from '@/components/ui/FlagIcon';
import { COUNTRIES, findByDialCode, DEFAULT_COUNTRY, type Country } from '@/lib/countries';

interface PhoneFieldProps {
  /** id of the number input (for the <label htmlFor>). */
  id: string;
  /** Selected dial code, e.g. "+91". */
  dialCode: string;
  onDialCodeChange: (dialCode: string) => void;
  /** National number (digits the user typed after the code). */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  /** id of the error element, wired to aria-describedby. */
  errorId?: string;
  required?: boolean;
  name?: string;
  /** Background + radius classes so the field matches the host form's inputs. */
  fieldClassName?: string;
}

export default function PhoneField({
  id,
  dialCode,
  onDialCodeChange,
  value,
  onChange,
  onBlur,
  error,
  errorId,
  required,
  name = 'phone',
  fieldClassName = 'bg-muted rounded-md',
}: PhoneFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const selected: Country = findByDialCode(dialCode) ?? DEFAULT_COUNTRY;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || c.iso2.toLowerCase() === q
    );
  }, [query]);

  // Close on outside click / Escape; focus the search box when opening.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open]);

  const pick = (c: Country) => {
    onDialCodeChange(c.dialCode);
    setOpen(false);
    setQuery('');
  };

  const inputBorder = error
    ? 'border-error focus:border-error focus:ring-error/30'
    : 'border-border focus:border-primary focus:ring-primary/30';

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex gap-2">
        {/* Country-code trigger */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={`Country code, currently ${selected.name} ${selected.dialCode}`}
          className={`flex shrink-0 items-center gap-1.5 min-h-11 px-3 border font-body text-foreground transition-colors focus:outline-none focus:ring-2 ${fieldClassName} ${inputBorder}`}
        >
          <FlagIcon iso2={selected.iso2} className="h-3.5 w-5" />
          <span className="text-sm tabular-nums">{selected.dialCode}</span>
          <Icon
            name="ChevronDownIcon"
            size={16}
            aria-hidden="true"
            className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {/* National number */}
        <input
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder="Phone number"
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error && errorId ? errorId : undefined}
          className={`w-full min-h-11 px-4 py-3 border focus:outline-none focus:ring-2 transition-colors ${fieldClassName} ${inputBorder}`}
        />
      </div>

      {open && (
        <div className="absolute z-30 mt-2 w-full max-w-xs rounded-md border border-border bg-surface shadow-dramatic">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={16}
                aria-hidden="true"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code"
                aria-label="Search country or dial code"
                className="w-full min-h-11 pl-8 pr-3 py-2 bg-muted border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-label="Country codes"
            className="max-h-60 overflow-y-auto py-1"
          >
            {filtered.map((c) => (
              <li
                key={`${c.iso2}-${c.dialCode}`}
                role="option"
                aria-selected={c.dialCode === dialCode}
              >
                <button
                  type="button"
                  onClick={() => pick(c)}
                  className={`flex w-full items-center gap-3 min-h-11 px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                    c.dialCode === dialCode ? 'bg-primary/10 text-primary' : 'text-foreground'
                  }`}
                >
                  <FlagIcon iso2={c.iso2} className="h-3.5 w-5" />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground tabular-nums">{c.dialCode}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-muted-foreground">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
